import React, { useState } from 'react';
import { Check, X, AlertTriangle, CalendarIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { CompletionRequirements } from '@/hooks/useJobCompletion';

interface CompletionValidationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  requirements: CompletionRequirements | null;
  onSubmit: (notes?: string, deliveryDate?: Date) => Promise<boolean>;
  loading: boolean;
}

export const CompletionValidationModal: React.FC<CompletionValidationModalProps> = ({
  open,
  onOpenChange,
  requirements,
  onSubmit,
  loading
}) => {
  const [completionNotes, setCompletionNotes] = useState('');
  const [deliveryDate, setDeliveryDate] = useState<Date>();

  const handleSubmit = async () => {
    const success = await onSubmit(
      completionNotes.trim() || undefined,
      deliveryDate
    );
    if (success) {
      setCompletionNotes('');
      setDeliveryDate(undefined);
      onOpenChange(false);
    }
  };

  const RequirementItem = ({ 
    passed, 
    label, 
    details 
  }: { 
    passed: boolean; 
    label: string; 
    details?: string;
  }) => (
    <div className="flex items-start gap-3 p-3 rounded-lg border bg-card">
      <div className="flex-shrink-0 mt-0.5">
        {passed ? (
          <Check className="h-5 w-5 text-green-600" />
        ) : (
          <X className="h-5 w-5 text-destructive" />
        )}
      </div>
      <div className="flex-1">
        <div className="font-medium text-sm">{label}</div>
        {details && (
          <div className="text-xs text-muted-foreground mt-1">{details}</div>
        )}
      </div>
    </div>
  );

  if (!requirements) {
    return null;
  }

  const canSubmit = requirements.can_submit;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            Mark Job Complete
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            Please review the completion requirements below:
          </div>

          <div className="space-y-3">
            <RequirementItem
              passed={requirements.milestones_complete}
              label={`All milestones completed (${requirements.completed_milestones}/${requirements.total_milestones})`}
              details={requirements.total_milestones === 0 ? "No milestones defined for this job" : undefined}
            />

            <RequirementItem
              passed={requirements.proof_of_delivery_uploaded}
              label="Proof of Delivery uploaded"
              details="Upload a Proof of Delivery using the Documents tab"
            />

            <RequirementItem
              passed={requirements.no_pending_modifications}
              label="No outstanding modification requests"
              details="All purchase order modifications must be resolved"
            />

            <RequirementItem
              passed={requirements.job_status_valid}
              label="Job status allows completion"
              details="Job must be awarded, in progress, or previously rejected"
            />
          </div>

          {canSubmit && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="delivery-date">
                  Date Delivered <span className="text-destructive">*</span>
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !deliveryDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {deliveryDate ? format(deliveryDate, "PPP") : <span>Select delivery date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={deliveryDate}
                      onSelect={setDeliveryDate}
                      disabled={(date) => date > new Date()}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="completion-notes">
                  Completion Notes (Optional)
                </Label>
                <Textarea
                  id="completion-notes"
                  placeholder="Add any notes about the job completion..."
                  value={completionNotes}
                  onChange={(e) => setCompletionNotes(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}

          {!canSubmit && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
              <X className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
              <div className="text-sm text-destructive">
                Requirements not met - Cannot submit completion
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {canSubmit && (
            <Button 
              onClick={handleSubmit}
              disabled={loading || !deliveryDate}
            >
              {loading ? "Submitting..." : "Submit for Client Review"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};